import moment from 'moment'

export const formatEmissionTonnes = (kilograms?: number): string => {
  if (kilograms == null || Number.isNaN(kilograms)) return '—';
  const tonnes = kilograms / 1000;
  return `${new Intl.NumberFormat('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(tonnes)} tCO₂e`;
};

// Scale-aware variant for small figures (a single month's entry can be tens of kg,
// which reads as 0.02 tCO₂e at two decimal places): kg below one tonne, tonnes above.
export const formatEmissionAmount = (kilograms?: number | null): string => {
  if (kilograms == null || Number.isNaN(kilograms)) return '—';
  if (Math.abs(kilograms) < 1000) {
    return `${new Intl.NumberFormat('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(kilograms)} kgCO₂e`;
  }
  return formatEmissionTonnes(kilograms);
};

export const formatPrimaryDataCoverage = (primaryDataPercent?: number | null): string => {
  if (primaryDataPercent == null || Number.isNaN(primaryDataPercent)) return '—';
  return `${Math.round(primaryDataPercent)}% activity data`;
};

/** Chip label for detail header: High / Medium / Low data quality. */
export const formatDataQualityTierLabel = (primaryDataPercent?: number | null): string => {
  if (primaryDataPercent == null || Number.isNaN(primaryDataPercent)) return '—';
  const p = Math.round(primaryDataPercent);
  if (p >= 80) return `High data quality (${p}%)`;
  if (p >= 30) return `Medium data quality (${p}%)`;
  return `Low data quality (${p}%)`;
};

/** Compact line for calculation cards, e.g. "High quality (100%)". */
export const formatDataQualityCompact = (primaryDataPercent?: number | null): string => {
  if (primaryDataPercent == null || Number.isNaN(primaryDataPercent)) return '—';
  const p = Math.round(primaryDataPercent);
  if (p >= 80) return `High quality (${p}%)`;
  if (p >= 30) return `Medium quality (${p}%)`;
  return `Low quality (${p}%)`;
};

/**
 * Per-calculation quality tier for the Calculations tab (no %).
 * Based on method type — not the emission-level blended primaryDataPercent.
 */
export const formatCalculationQualityLabel = (calc: {
  type: string
  dataType?: { dataQuality?: string | null } | null
}): string => {
  // Prefer the stored data-quality tier (driven by the factor's provenance) over the method-type
  // heuristic — a PRODUCT factor is only "High" when it comes from a supplier/EPD, not because it is a product.
  const tier = calc.dataType?.dataQuality;
  if (tier) return `${tier} quality`;
  // Fallback when the dataType relation isn't included on the payload.
  if (calc.type === 'SPEND') return 'Low quality';
  if (calc.type === 'ACTIVITY') return 'High quality';
  if (calc.type === 'MEASUREMENT') return 'High quality';
  if (calc.type === 'DIRECT') return 'High quality';
  // PRODUCT without provenance loaded: don't claim High — default to Medium.
  if (calc.type === 'PRODUCT') return 'Medium quality';
  return 'Medium quality';
};

/** Share of this calculation's kg within the selected-total kg (selected method rows only). */
export const formatContributionToSelectedLabel = (kg: number, selectedTotalKg: number): string | null => {
  if (selectedTotalKg <= 0 || !Number.isFinite(kg)) return null;
  const pct = Math.round((kg / selectedTotalKg) * 100);
  return `Contributes ${pct}% of selected total`;
};

export const formatKgCo2e = (kg: number): string => {
  return `${new Intl.NumberFormat('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(kg)} kg CO₂e`;
};

export const formatPercentVsPrimary = (altKg: number, primaryKg: number): string | null => {
  if (primaryKg <= 0 || !Number.isFinite(altKg)) return null;
  const raw = ((altKg - primaryKg) / primaryKg) * 100;
  const rounded = Math.round(raw);
  if (rounded === 0) return 'Same as primary';
  const sign = rounded > 0 ? '+' : '';
  return `${sign}${rounded}% vs primary`;
};

export const formatActivityDisplay = (activityLabel?: string, activityContext?: string): string => {
  const label = activityLabel?.trim();
  if (!label) return '—';
  const context = activityContext?.trim();
  if (!context) return label;
  const normalizedLabel = label.toLowerCase();
  const normalizedContext = context.toLowerCase();
  if (normalizedLabel === normalizedContext || normalizedLabel.endsWith(`- ${normalizedContext}`)) {
    return label;
  }
  return context ? `${label} - ${context}` : label;
};

type ConsumptionCalculation = {
  type: string;
  isSelected: boolean;
  isWtt: boolean | null;
  unitsConsumed: number | null;
  /** SPEND rows only: the spend as entered, before FX and inflation adjustment. */
  originalUnitsConsumed?: number | null;
  measurementUnit: { name: string } | null;
};

// Two decimals, never significant-digit rounding: a spend of 447,516.16 rendered as "447,500"
// reads as a rounded-off estimate of a figure the user entered to the penny.
const formatQuantity = (total: number): string =>
  new Intl.NumberFormat('en-GB', { maximumFractionDigits: 2 }).format(total);

// Money keeps its pence, so a spend total can be reconciled against the ledger it came from.
const formatMoney = (total: number): string =>
  new Intl.NumberFormat('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(total);

// Currency measurement units are named "symbol (CODE)": "£ (GBP)", "€ (EUR)". A record can carry an
// alternative calculation built by another method, and a floor-area estimate brings MEASUREMENT rows
// of its own (square foot, m2), so the spend's currency row is identified rather than assumed.
const isCurrencyUnit = (unitName?: string | null): boolean => !!unitName && /\([A-Z]{3}\)/.test(unitName);

const sharedUnitName = (calcs: ConsumptionCalculation[]): string | null => {
  const unitName = calcs[0]?.measurementUnit?.name;
  if (!unitName) return null;
  return calcs.some((c) => c.measurementUnit?.name !== unitName) ? null : unitName;
};

const sumConsumption = (
  calcs: ConsumptionCalculation[],
  valueOf: (calc: ConsumptionCalculation) => number | null | undefined = (calc) => calc.unitsConsumed,
  format: (total: number) => string = formatQuantity,
): string | null => {
  if (calcs.length === 0) return null;
  const unitName = sharedUnitName(calcs);
  if (!unitName) return null;
  const total = calcs.reduce((sum, c) => sum + (valueOf(c) ?? 0), 0);
  return `${format(total)} ${unitName}`;
};

/**
 * The money a spend emission cost, in the currency it was entered in.
 *
 * A SPEND row's `unitsConsumed` is the FX and inflation adjusted figure the factor is applied to,
 * not the amount spent, so summing it reports a deflated spend: 553,380.20 GBP of invoices against
 * a 2021 factor set sums to 447,516.16. The entered spend lives on the unselected currency
 * MEASUREMENT row of each calendar-year segment, with `originalUnitsConsumed` on the SPEND rows as
 * the fallback for legacy entries that predate those rows. That is the same ladder the spend edit
 * dialog seeds its form from and the export's Original Spend column uses (the backend's
 * `lib/spend-original/spend-original.helper.js` documents it in full). The converted figure is the
 * last resort, and only when neither source survived the page's filter.
 */
const spendConsumption = (
  calcs: ConsumptionCalculation[],
  selectedSpend: ConsumptionCalculation[],
): string | null => {
  const currencyRows = calcs.filter(
    (c) => c.type === 'MEASUREMENT' && c.unitsConsumed != null && isCurrencyUnit(c.measurementUnit?.name)
  );
  const entered = sumConsumption(currencyRows, (c) => c.unitsConsumed, formatMoney);
  if (entered) return entered;
  if (selectedSpend.every((c) => c.originalUnitsConsumed != null)) {
    return sumConsumption(selectedSpend, (c) => c.originalUnitsConsumed, formatMoney);
  }
  return sumConsumption(selectedSpend, (c) => c.unitsConsumed, formatMoney);
};

/**
 * Returns the summed consumption for an emission's selected, non-WTT calculations. Physical
 * consumption wins; for spend-only emissions the amount spent is shown with its currency unit
 * so it cannot read as a physical quantity. Shows "—" when units are mixed or data is absent.
 * Example outputs: "100.5 kWh", "50 l", "1,095.90 £ (GBP)"
 *
 * The list endpoint only ships the calculations that matched the page's filter. On a view scoped
 * to upstream rows (the Fuel and Energy Related Activities group page) every calculation is WTT,
 * so when no non-WTT calculation is present the WTT rows' own consumption is used instead of
 * rendering a dash against a populated record.
 */
export const formatConsumptionFromCalculations = (calcs?: ConsumptionCalculation[]): string => {
  if (!calcs || calcs.length === 0) return '—';
  const allSelected = calcs.filter((c) => c.isSelected);
  const selected = allSelected.some((c) => !c.isWtt) ? allSelected.filter((c) => !c.isWtt) : allSelected;
  const physical = sumConsumption(selected.filter((c) => c.type !== 'SPEND'));
  if (physical) return physical;
  const selectedSpend = selected.filter((c) => c.type === 'SPEND');
  if (selectedSpend.length === 0) return '—';
  return spendConsumption(calcs, selectedSpend) ?? '—';
};

type FactorCalculation = {
  isSelected: boolean;
  isWtt: boolean | null;
  factor?: {
    detailLevelTwo?: string | null;
    detailLevelThree?: string | null;
    detailLevelFour?: string | null;
    detailLevelFive?: string | null;
    activity?: string | null;
  } | null;
};

// A few factor sets carry their unit or a year in level four ("kWh", "Tonnes", "2020", "0"), which
// says nothing about what the factor is for.
const isUnitOrNumberLevel = (value: string): boolean => /^(\d+(\.\d+)?|kwh|tonnes)$/i.test(value);

const factorLabel = (factor: NonNullable<FactorCalculation['factor']>): string => {
  if (factor.activity?.trim()) return factor.activity.trim();
  const clean = (value?: string | null) => value?.trim() || '';
  const four = clean(factor.detailLevelFour);
  return [
    clean(factor.detailLevelThree) || clean(factor.detailLevelTwo),
    isUnitOrNumberLevel(four) ? '' : four,
    clean(factor.detailLevelFive),
  ].filter(Boolean).join(' · ');
};

/**
 * The emission factor behind a row, for the line under its activity. Rows that share a friendly
 * category only differ by factor: "Waste Management" rows read "Concrete · Open-loop",
 * "Plastics: HDPE (incl. forming) · Landfill" and so on. Uses the most specific factor levels
 * (the first two only restate the category), or the activity for a spend factor. Gated on the
 * same calculations as the consumption column; distinct factors are joined with "; ".
 */
export const formatFactorDetailFromCalculations = (calcs?: FactorCalculation[]): string => {
  if (!calcs || calcs.length === 0) return '';
  const allSelected = calcs.filter((c) => c.isSelected);
  const selected = allSelected.some((c) => !c.isWtt) ? allSelected.filter((c) => !c.isWtt) : allSelected;
  const labels = selected
    .map((c) => (c.factor ? factorLabel(c.factor) : ''))
    .filter(Boolean);
  return Array.from(new Set(labels)).join('; ');
};

/**
 * Where an emission came from, from its calculations: the selected non-upstream calculation's
 * recorded source (an upstream row inherits its parent's), and for an integration the ledger it
 * was committed from. A row older than the source stamp carries null, which reads as manual entry.
 */
export const resolveSourceFromCalculations = (
  calcs?: Array<{ isSelected?: boolean; isWtt?: boolean | null; source?: string | null; providerCode?: string | null }>
): { source: string | null; providerCode: string | null } => {
  if (!calcs || calcs.length === 0) return { source: null, providerCode: null };
  const selected = calcs.filter((c) => c.isSelected);
  const ordered = [...selected.filter((c) => !c.isWtt), ...selected.filter((c) => c.isWtt), ...calcs];
  const providerCode = ordered.find((c) => c.providerCode)?.providerCode ?? null;
  const source = ordered.find((c) => c.source)?.source ?? null;
  return { source: providerCode ? 'INTEGRATION' : source, providerCode };
};

/**
 * Emission-level FLAG state derived from its calculations. Creation stamps every calculation with the
 * same values, but legacy rows can diverge, so treat the emission as FLAG when any calculation is
 * flagged and take the first category present.
 */
export const resolveFlagFromCalculations = (
  calcs?: Array<{ isFlag?: boolean | null; flagCategory?: string | null }>
): { isFlag: boolean; flagCategory: string | null } => {
  const isFlag = !!calcs?.some((c) => c.isFlag === true);
  if (!isFlag) return { isFlag: false, flagCategory: null };
  return { isFlag: true, flagCategory: calcs?.find((c) => c.flagCategory)?.flagCategory ?? null };
};

/** Which category editor an emission supports, or null when it supports none. */
export type CategoryEditMode = 'SPEND' | 'ACTIVITY' | null

/**
 * Decides whether an emission's factor can be re-mapped, and with which picker.
 *
 * Classifies on the primary calculations only, matching `calculationIsPrimary` in the backend's
 * emissionCalculation controller: the bulk-edit endpoint applies that same rule when it decides what
 * it can update, so the dialog offers the editor exactly when the save will succeed. The payload's
 * `methodLabel` is deliberately not used: it is derived from a filter that does not exclude
 * MEASUREMENT rows, and is only correct today because currency-conversion rows are written unselected.
 *
 * Returns null for a mixed (hybrid) emission, for DIRECT/PRODUCT, and when nothing is selected.
 */
export const resolveCategoryEditMode = (
  calcs?: Array<{ type: string; isSelected: boolean; isWtt: boolean | null }>
): CategoryEditMode => {
  const primaries = (calcs ?? []).filter(
    (c) => c.type !== 'MEASUREMENT' && c.isSelected && !c.isWtt
  )
  if (primaries.length === 0) return null
  if (primaries.every((c) => c.type === 'SPEND')) return 'SPEND'
  if (primaries.every((c) => c.type === 'ACTIVITY')) return 'ACTIVITY'
  return null
}

/** Single day: `07 Jan 2026`. Same month range: `07–10 Jan 2026`. Otherwise full dates with en-dash. */
export const formatEmissionPeriodRange = (startDate: string, endDate: string): string => {
  const start = moment(startDate)
  const end = moment(endDate)
  if (!start.isValid() || !end.isValid()) return '—'
  if (start.isSame(end, 'day')) return start.format('DD MMM YYYY')
  if (start.isSame(end, 'month') && start.isSame(end, 'year')) {
    return `${start.format('DD')}–${end.format('DD')} ${end.format('MMM YYYY')}`
  }
  return `${start.format('DD MMM YYYY')} – ${end.format('DD MMM YYYY')}`
}

/**
 * The period one calculation card covers, or null when it covers the whole record.
 *
 * A record that spans a calendar-year boundary is calculated one segment per calendar year, each
 * resolving its own factor year, inflation adjustment and FX, so an April-to-March financial year
 * produces two cards that are identical apart from the months they cover and the emission that
 * follows. Naming the period is what tells them apart. A single-segment record covers exactly the
 * period in the header above the cards, so the label would only repeat it and is left off.
 */
export const formatCalculationPeriodLabel = (
  calcs: Array<{ startDate?: string | null; endDate?: string | null }>,
  record: { startDate: string; endDate: string },
): string | null => {
  const starts = calcs.map((c) => c.startDate).filter((d): d is string => !!d)
  const ends = calcs.map((c) => c.endDate).filter((d): d is string => !!d)
  if (starts.length === 0 || ends.length === 0) return null
  const start = starts.reduce((earliest, d) => (moment(d).isBefore(earliest) ? d : earliest))
  const end = ends.reduce((latest, d) => (moment(d).isAfter(latest) ? d : latest))
  const coversWholeRecord =
    moment(start).isSame(record.startDate, 'day') && moment(end).isSame(record.endDate, 'day')
  return coversWholeRecord ? null : formatEmissionPeriodRange(start, end)
}
